// Miroir front de services/ephemeralStory/storyQuota.service.js (voissa-api-rest).
//
// Les quotas de stories accordés par la certification introduisent un TROISIÈME état, qui
// n'existait nulle part ailleurs dans l'app : entre « j'ai le droit » et « je ne l'ai pas », il y
// a désormais « j'ai le droit mais je l'ai épuisé aujourd'hui ». Les confondre produit la pire
// microcopy possible — proposer « certifiez votre profil » à quelqu'un qui EST certifié.
//
// L'API reste seule juge (402 au moment de l'action) ; ce module ne sert qu'à l'affichage.
// Les VALEURS des plafonds ne sont pas dupliquées ici : elles arrivent dans `user.storyQuota`,
// calculées par l'API, et le compteur est patché localement au retour de POST /views.

export const STORY_QUOTA_KIND = {
    PUBLISH: 'publish',
    VIEW: 'view',
};

// Niveaux d'accès, alignés sur STORY_ACCESS côté API.
export const STORY_ACCESS = {
    FULL: 'full',   // capability pleine (abonné / admin) : illimité
    QUOTA: 'quota', // capability quotaée (certifié) : plafonné à la journée
    NONE: 'none',   // aucun droit
};

// Nature du quota -> capability pleine puis quotaée. L'ordre EST la règle : la pleine gagne
// toujours, sinon un abonné certifié serait traité comme un membre plafonné.
const CAPS_BY_KIND = {
    [STORY_QUOTA_KIND.PUBLISH]: { full: 'canCreateStory', daily: 'canCreateStoryDaily' },
    [STORY_QUOTA_KIND.VIEW]: { full: 'canViewStories', daily: 'canViewStoriesDaily' },
};

/** Niveau d'accès d'après les seules capabilities (pas le compteur). */
export const resolveStoryAccess = (currentUser, kind) => {
    const conf = CAPS_BY_KIND[kind];
    if (!conf) return STORY_ACCESS.NONE;
    const caps = currentUser?.capabilities || {};
    if (caps[conf.full]) return STORY_ACCESS.FULL;
    if (caps[conf.daily]) return STORY_ACCESS.QUOTA;
    return STORY_ACCESS.NONE;
};

/**
 * État du quota tel que l'API l'a renvoyé ({ access, limit, used, remaining }), ou null.
 *
 * Lu sous `quota.story` : l'API regroupe ses plafonds journaliers par domaine plutôt que d'ajouter
 * un champ racine par famille, de sorte qu'un futur quota vienne s'y ranger sans toucher au front.
 */
export const getStoryQuota = (currentUser, kind) => currentUser?.quota?.story?.[kind] || null;

/**
 * Le membre peut-il encore agir maintenant ? true pour un accès illimité, true pour un quotaé
 * qui a du reste, false sinon. On se rabat sur le niveau d'accès si `quota.story` n'est pas encore
 * chargé — l'API tranchera de toute façon.
 */
export const hasStoryQuotaLeft = (currentUser, kind) => {
    const access = resolveStoryAccess(currentUser, kind);
    if (access === STORY_ACCESS.FULL) return true;
    if (access === STORY_ACCESS.NONE) return false;
    const quota = getStoryQuota(currentUser, kind);
    return quota ? quota.remaining > 0 : true;
};

/** true UNIQUEMENT dans le troisième état : le droit existe mais la journée est consommée. */
export const isStoryQuotaExhausted = (currentUser, kind) =>
    resolveStoryAccess(currentUser, kind) === STORY_ACCESS.QUOTA
    && !hasStoryQuotaLeft(currentUser, kind);

/**
 * Applique à un objet currentUser le quota renvoyé par POST /:storyId/views. Sert au patch du
 * cache React Query : le compteur bouge sans refetch, donc les vignettes se refloutent dès que la
 * dernière story autorisée a été vue.
 */
export const withStoryQuota = (currentUser, kind, quota) => {
    if (!currentUser || !quota) return currentUser;
    return {
        ...currentUser,
        quota: {
            ...(currentUser.quota || {}),
            story: { ...(currentUser.quota?.story || {}), [kind]: quota },
        },
    };
};
